import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { JoinRequest } from '../types/database';
import { useAuth } from './useAuth';

/** Fetch all pending join requests for bands the current user owns/admins */
export function useMyBandJoinRequests() {
  const { user } = useAuth();
  const [requests, setRequests] = useState<JoinRequest[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchRequests = useCallback(async () => {
    if (!user) {
      setRequests([]);
      setLoading(false);
      return;
    }

    // Get bands where user is admin/owner
    const { data: myBands } = await supabase
      .from('band_members')
      .select('band_id')
      .eq('user_id', user.id)
      .in('role', ['owner', 'admin'])
      .eq('is_accepted', true);

    if (!myBands || myBands.length === 0) {
      setRequests([]);
      setLoading(false);
      return;
    }

    const bandIds = myBands.map((b) => b.band_id);

    const { data } = await supabase
      .from('join_requests')
      .select('*, profile:profiles(*), band:bands(*)')
      .in('band_id', bandIds)
      .eq('status', 'pending')
      .order('created_at', { ascending: false });

    if (data) {
      setRequests(data as unknown as JoinRequest[]);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  return { requests, loading, refetch: fetchRequests };
}

/** Fetch join requests for a specific band (for manage page) */
export function useBandJoinRequests(bandId: string | undefined) {
  const [requests, setRequests] = useState<JoinRequest[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchRequests = useCallback(async () => {
    if (!bandId) {
      setRequests([]);
      setLoading(false);
      return;
    }

    const { data } = await supabase
      .from('join_requests')
      .select('*, profile:profiles(*)')
      .eq('band_id', bandId)
      .eq('status', 'pending')
      .order('created_at', { ascending: false });

    if (data) {
      setRequests(data as unknown as JoinRequest[]);
    }
    setLoading(false);
  }, [bandId]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  return { requests, loading, refetch: fetchRequests };
}

/** Check if the current user has already requested to join a band */
export function useMyJoinRequestStatus(bandId: string | undefined) {
  const { user } = useAuth();
  const [status, setStatus] = useState<'none' | 'pending' | 'approved' | 'rejected'>('none');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!bandId || !user) {
      setStatus('none');
      setLoading(false);
      return;
    }

    async function check() {
      const { data } = await supabase
        .from('join_requests')
        .select('status')
        .eq('band_id', bandId!)
        .eq('user_id', user!.id)
        .single();

      if (data) {
        setStatus(data.status as 'pending' | 'approved' | 'rejected');
      } else {
        setStatus('none');
      }
      setLoading(false);
    }
    check();
  }, [bandId, user]);

  return { status, loading };
}

/** Submit a join request */
export async function submitJoinRequest(
  bandId: string,
  userId: string,
  message: string
): Promise<{ error: string | null }> {
  const { error } = await supabase.from('join_requests').insert({
    band_id: bandId,
    user_id: userId,
    message,
  });

  if (error) return { error: error.message };
  return { error: null };
}

/** Approve a join request - adds user as band member */
export async function approveJoinRequest(
  requestId: string,
  bandId: string,
  userId: string,
  reviewerId: string
): Promise<{ error: string | null }> {
  // Get user email
  const { data: profile } = await supabase
    .from('profiles')
    .select('email')
    .eq('id', userId)
    .single();

  if (!profile) return { error: 'User not found' };

  // Update request status
  const { error: updateError } = await supabase
    .from('join_requests')
    .update({
      status: 'approved',
      reviewed_by: reviewerId,
      reviewed_at: new Date().toISOString(),
    })
    .eq('id', requestId);

  if (updateError) return { error: updateError.message };

  // Add user as band member
  const { error: memberError } = await supabase.from('band_members').insert({
    band_id: bandId,
    user_id: userId,
    email: profile.email,
    role: 'member',
    is_accepted: true,
    accepted_at: new Date().toISOString(),
  });

  if (memberError) return { error: memberError.message };

  return { error: null };
}

/** Reject a join request */
export async function rejectJoinRequest(
  requestId: string,
  reviewerId: string
): Promise<{ error: string | null }> {
  const { error } = await supabase
    .from('join_requests')
    .update({
      status: 'rejected',
      reviewed_by: reviewerId,
      reviewed_at: new Date().toISOString(),
    })
    .eq('id', requestId);

  if (error) return { error: error.message };
  return { error: null };
}
